/** Copied from:
 * https://github.com/BigBasket/ra-components/pull/23
 *
 * But, updated to @mui/system
 * ... until ra-components updates to not use @mui/styled
 */
import LabelIcon from '@mui/icons-material/Label';
import DefaultIcon from '@mui/icons-material/ViewList';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { styled } from '@mui/material/styles';
import classnames from 'classnames';
import React, { useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { DashboardMenuItem, MenuItemLink, useResourceDefinitions, useSidebarState, useTranslate } from 'react-admin';
import CustomMenuItem from './CustomMenuItem';
import logo from '../logo.svg';

const PREFIX = 'RaTreeMenu';

const classes = {
  main: `${PREFIX}-main`,
  open: `${PREFIX}-open`,
  closed: `${PREFIX}-closed`,
};

const StyledMenu = styled('div')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const sidebarBg    = isDark ? '#0D1117' : '#0F172A';
  const primaryText  = isDark ? '#F1F5F9' : '#F8FAFC';
  const mutedText    = isDark ? 'rgba(241,245,249,0.45)' : 'rgba(248,250,252,0.5)';
  const hoverBg      = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.08)';
  const activeBg     = isDark ? 'rgba(129,140,248,0.18)' : 'rgba(255,255,255,0.15)';
  const activeText   = isDark ? '#A5B4FC' : '#ffffff';
  const activeIcon   = isDark ? '#818CF8' : '#ffffff';

  return {
    'background':   sidebarBg,
    'position':     'fixed',
    'left':         0,
    'top':          0,
    'bottom':       0,
    'width':        240,
    'zIndex':       1101,
    'borderRight':  `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)'}`,
    'overflowY':    'auto',
    'overflowX':    'hidden',
    'display':      'flex',
    'flexDirection':'column',

    'li, a': {
      'color':          primaryText,
      'fontSize':       '13px',
      'fontWeight':     400,
      'borderRadius':   '8px',
      'margin':         '1px 8px',
      'padding':        '8px 12px',
      'transition':     'background 140ms ease, color 140ms ease',
      'letterSpacing':  '-0.01em',

      '&.RaMenuItemLink-active': {
        'background':  `${activeBg} !important`,
        'color':       activeText,
        'fontWeight':  600,
        '.MuiListItemIcon-root': { color: activeIcon },
      },

      '&:hover': { background: hoverBg },
    },

    '.MuiListItemIcon-root': {
      color:    mutedText,
      minWidth: 32,
      '& svg': { fontSize: 18 },
    },

    '.MuiListItemText-root span': {
      fontSize:      13,
      fontWeight:    'inherit',
      letterSpacing: '-0.01em',
    },

    // Section headers
    '.MuiListSubheader-root': {
      background:    'transparent',
      color:         mutedText,
      fontSize:      '10px',
      fontWeight:    700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      padding:       '16px 20px 6px',
      lineHeight:    1,
    },

    [`& .${classes.main}`]: {
      display:        'flex',
      flexDirection:  'column',
      justifyContent: 'flex-start',
      flex:           1,
      paddingBottom:  16,
    },
  };
});

interface TreeMenuProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  dense?: boolean;
  hasDashboard?: boolean;
  onMenuClick?: (event?: unknown) => void;
  logout?: ReactNode;
  dashboardlabel?: string;
  setMenuColors?: boolean;
}

const TreeMenu: React.FC<TreeMenuProps> = (props) => {
  const {
    className,
    dense,
    hasDashboard,
    onMenuClick = () => null,
    logout,
    dashboardlabel = 'Dashboard',
    setMenuColors = true,
    // Filter out react-admin props that shouldn't be passed to DOM
    // @ts-expect-error - appBarAlwaysOn is passed by react-admin but not in our interface
    appBarAlwaysOn,
    ...rest
  } = props;

  const translate = useTranslate();
  const [open] = useSidebarState();
  const pathname = window.location.hash;
  let allResources = useResourceDefinitions();
  const resources = Object.keys(allResources).map((name) => allResources[name]);
  const hasList = (resource) => resource.hasList;

  const handleToggle = (parent) => {
    setState((state) => ({ [parent]: !state[parent] }));
  };

  const isParent = (resource) => !!resource?.options?.isMenuParent;

  const isOrphan = (resource) =>
    !resource?.options?.hasOwnProperty?.('menuParent') && !resource?.options?.hasOwnProperty?.('isMenuParent');

  const isChildOfParent = (resource, parentResource) => resource?.options?.menuParent == parentResource?.name;

  const getResourceName = (slug) => {
    if (!slug) {
      return '';
    }

    var words = slug.toString().split('_');

    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      words[i] = word.charAt(0).toUpperCase() + word.slice(1);
    }

    return words.join(' ');
  };

  const getPrimaryTextForResource = (resource) => {
    let resourcename = '';

    if (resource?.options?.label) {
      resourcename = resource.options.label;
    } else if (resource?.name) {
      resourcename = translate(`resources.${resource.name}.name`);

      if (resourcename.startsWith('resources.')) {
        resourcename = getResourceName(resource.name);
      }
    }

    return resourcename;
  };

  const MenuItem = (resource) => (
    <MenuItemLink
      key={resource.name}
      to={`/${encodeURIComponent(resource.name)}`}
      primaryText={getPrimaryTextForResource(resource)}
      leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
      onClick={onMenuClick}
      dense={dense}
      sidebarIsOpen={open}
    />
  );

  const mapParentStack = (parentResource) => {
    return (
      <CustomMenuItem
        key={parentResource.name}
        handleToggle={() => handleToggle(parentResource.name)}
        isOpen={state[parentResource.name] || parentActiveResName === parentResource.name}
        sidebarIsOpen={open}
        name={getPrimaryTextForResource(parentResource)}
        icon={parentResource.icon ? <parentResource.icon /> : <LabelIcon />}
        dense={dense}
        setMenuColors={false}
      >
        {
          // eslint-disable-next-line
          resources
            .filter((resource) => isChildOfParent(resource, parentResource) && hasList(resource))
            .map((childResource) => {
              return MenuItem(childResource);
            })
        }
      </CustomMenuItem>
    );
  };

  const mapIndependent = (independentResource) => hasList(independentResource) && MenuItem(independentResource);

  const initialExpansionState = {};

  let parentActiveResName = null;

  resources?.forEach((resource) => {
    if (isParent(resource)) {
      initialExpansionState[resource.name] = false;
    } else if (
      pathname.startsWith(`#/${encodeURIComponent(resource.name)}`) &&
      resource?.options?.hasOwnProperty?.('menuParent')
    ) {
      parentActiveResName = resource.options.menuParent;
    }
  });

  const [state, setState] = useState(initialExpansionState);
  const resRenderGroup: React.ReactNode[] = [];

  resources &&
    resources.length > 0 &&
    resources.forEach((resource) => {
      if (isParent(resource)) resRenderGroup.push(mapParentStack(resource));
      if (isOrphan(resource)) resRenderGroup.push(mapIndependent(resource));
    });

  return (
    <StyledMenu>
      {/* Spacer to match AppHeader brand area height */}
      <div style={{ height: 60, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)' }} />

      <div
        className={classnames(classes.main, className, {
          [classes.open]: open,
          [classes.closed]: !open,
        })}
        {...rest}
      >
        <DashboardMenuItem onClick={onMenuClick} dense={dense} sidebarIsOpen={open} primaryText={dashboardlabel} />
        {resRenderGroup}
        <MenuItemLink
          key='provisioner'
          to='/provisioner'
          primaryText='Provisioner'
          leftIcon={<SystemUpdateAltIcon />}
          onClick={onMenuClick}
          dense={dense}
          sidebarIsOpen={open}
        />
        <MenuItemLink
          key='audit-log'
          to='/audit-log'
          primaryText='Audit Log'
          leftIcon={<HistoryIcon />}
          onClick={onMenuClick}
          dense={dense}
          sidebarIsOpen={open}
        />
        <MenuItemLink
          key='file-manager'
          to='/file-manager'
          primaryText='File Manager'
          leftIcon={<FolderIcon />}
          onClick={onMenuClick}
          dense={dense}
          sidebarIsOpen={open}
        />
      </div>
    </StyledMenu>
  );
};

export default TreeMenu;
